import { getGameBasicInfo } from '../services/game';
import {
  createRouteOnlyAnalysisUrlInfoHandler,
  flattenUrlCollectionRoutes,
  getBaseUrlCollectionSuccessResult,
  getDefaultUrlCollectionAnalysisUrlInfoResult,
  getUrlCollectionEntityPageName,
  isUrlCollectionRouteMatched,
} from '@home/utils/urlCollectionCommon';
import { getUrlObject } from '@home/utils/urlConfig';
import { mergeUrlCollectionData, parseUrlCollectionData } from '@home/utils/urlCollectionData';
import {
  EntityType,
  IAnalysisUrlInfoResult,
  Permission,
  Status,
  URL_COLLECTION_ANALYSIS_FAILURE_REASON,
} from '@/pages/home/enums/urlCollection';
import { DBSystemCode } from '@home/_config/config';
import opinionRouters from './routers';

import type {
  IRestoreDefaultUrlCollectionItemParams,
  IRestoreDefaultUrlCollectionItemResult,
} from '@home/types/urlCollection';

const FLATTENED_ROUTES = flattenUrlCollectionRoutes(opinionRouters);
const OPINION_DEFAULT_SHORTCUT_URL = '/v2/opinion/Overview/KeyMetrics?gameid=:entityId';

/**
 * 依赖 sessionStorage 传递参数的详情页路由，无法通过保存链接进入
 * 这些页面通过 goStatsDetailAction 跳转，参数仅存在于 sessionStorage 中
 */
const UNSAVEABLE_DETAIL_ROUTES = [
  '/opinion/Stats/Detail',
  '/opinion/Overview/KeyMetrics/EventDetails',
  '/opinion/SteamPerformance/VersionAnalysis/Details',
  '/opinion/Detail',
];

/**
 * 检查当前路径是否匹配任何已注册的舆情路由
 * @param pathname - 当前路径（如 /opinion/Overview）
 * @returns 是否匹配
 */
export function isOpinionRouteMatched(pathname: string): boolean {
  return isUrlCollectionRouteMatched(pathname, FLATTENED_ROUTES);
}

export interface IEntityDetails {
  entity_type: EntityType[];
  entity_id: string;
  entity_name: string;
  entity_cover: string;
  permission: Permission;
}

export interface IEntityDetailsParams {
  entity_type?: EntityType;
  entity_id?: string;
  [key: string]: any;
}

/**
 * 根据实体 ID 查询实体详情信息
 * 供首页收藏卡片展示实体的实时信息（游戏名/封面、公司名/封面）
 */
export async function getEntityDetailsById(
  params: IEntityDetailsParams,
): Promise<IEntityDetails | null> {
  const { entity_id } = params;
  if (!entity_id) {
    return null;
  }

  try {
    const response = await getGameBasicInfo({
      edition_unified_ids: [entity_id],
    });
    const item = response?.list?.[entity_id];

    if (item) {
      const entityTypes = collectEntityTypes(item);
      const hasPermission = getOpinionPermission(item);

      return {
        entity_type: entityTypes,
        entity_id: item.edition_unified_id || entity_id,
        entity_name: item.entity_name || item.entity_name_en || item.entity_name_cn || '',
        entity_cover: item.cover || item.entity_image || '',
        permission: hasPermission ? Permission.HasPermission : Permission.NoPermission,
      };
    }
    return null;
  } catch (error) {
    console.error('[实体详情查询] 接口调用失败', error);
    return null;
  }
}

/**
 * 舆情 URL 收藏解析。
 *
 * 仅解析总入口传入的 pathname + search，识别 gameid 后拉取实体信息。
 *
 * @param params.url - 收藏的 url
 * @returns 解析结果
 */
export const analysisUrlInfo = createRouteOnlyAnalysisUrlInfoHandler({
  systemCode: DBSystemCode.Opinions,
  isRouteMatched: isOpinionRouteMatched,
  getResult: async ({ url, pagePath, urlObj, baseSuccessResult }): Promise<IAnalysisUrlInfoResult | null> => {
    const gameId = extractGameIdFromUrl(urlObj);

    // 检查是否为依赖 sessionStorage 的详情页路由（无法通过保存链接正确展示）
    if (UNSAVEABLE_DETAIL_ROUTES.some((routePath) => pagePath?.includes(routePath))) {
      return {
        ...getDefaultUrlCollectionAnalysisUrlInfoResult(url, urlObj, {
          permission: baseSuccessResult.permission,
          status: Status.Unavailable,
          failureReason: URL_COLLECTION_ANALYSIS_FAILURE_REASON.Unavailable,
          system: DBSystemCode.Opinions,
          pagePath,
        }),
        page_name: baseSuccessResult.page_name,
      };
    }

    if (!gameId) {
      return null;
    }

    const entityInfo = await getEntityDetailsById({ entity_id: gameId });

    if (!entityInfo) {
      return {
        ...getDefaultUrlCollectionAnalysisUrlInfoResult(url, urlObj, {
          permission: baseSuccessResult.permission,
          status: baseSuccessResult.status,
          failureReason: URL_COLLECTION_ANALYSIS_FAILURE_REASON.Unavailable,
          system: DBSystemCode.Opinions,
          pagePath,
        }),
        page_name: baseSuccessResult.page_name,
      };
    }

    return {
      ...baseSuccessResult,
      permission: entityInfo.permission,
      success: entityInfo.permission === Permission.HasPermission,
      failure_reason: entityInfo.permission === Permission.HasPermission
        ? undefined
        : URL_COLLECTION_ANALYSIS_FAILURE_REASON.NoPermission,
      status: entityInfo.permission === Permission.HasPermission
        ? Status.Available
        : Status.Unavailable,
      entity_type: entityInfo.entity_type,
      entity_id: entityInfo.entity_id,
      entity_name: entityInfo.entity_name,
      entity_cover: entityInfo.entity_cover,
      page_name: getUrlCollectionEntityPageName(baseSuccessResult.page_name, entityInfo.entity_name || ''),
    };
  },
});

function collectEntityTypes(item: any): EntityType[] {
  return item.entity_type ? [item.entity_type] : [EntityType.PC];
}

function getOpinionPermission(item: any): boolean {
  if (typeof item?.opinion_permissions === 'boolean') {
    return item.opinion_permissions;
  }

  if (typeof item?.permission === 'boolean') {
    return item.permission;
  }

  return true;
}

function extractGameIdFromUrl(urlObj?: URL | null): string {
  return decodeURIComponent(urlObj?.searchParams.get('gameid') || '');
}

function getOpinionDefaultShortcutName(pageName = '', entityName = ''): string {
  if (!entityName) {
    return pageName;
  }

  if (!pageName) {
    return entityName;
  }

  return `${entityName} ${pageName}`;
}

export async function restoreDefaultShortcutItem(
  params: IRestoreDefaultUrlCollectionItemParams,
): Promise<IRestoreDefaultUrlCollectionItemResult> {
  if (!params?.entity_id) {
    return {};
  }

  const nextUrl = OPINION_DEFAULT_SHORTCUT_URL.replace(':entityId', params.entity_id);
  const nextData = parseUrlCollectionData(params.data);
  const nextUrlObj = getUrlObject(nextUrl);
  const baseSuccessResult = getBaseUrlCollectionSuccessResult(nextUrl, nextUrlObj, {
    system: DBSystemCode.Opinions,
    pagePath: nextUrl,
  });
  const entityName = params.entity_name || '';
  const entityType = params.entity_type ? [params.entity_type] : [];
  const nextName = entityName
    ? getOpinionDefaultShortcutName(baseSuccessResult.page_name, entityName)
    : params.name;

  return {
    url: nextUrl,
    name: nextName,
    data: mergeUrlCollectionData(params.data, {
      ...nextData,
      entity_type: entityType,
      entity_id: params.entity_id,
      entity_name: entityName,
      entity_cover: params.entity_cover,
      page_name: nextName || nextData.page_name || params.name,
      page_path: baseSuccessResult.page_path || nextData.page_path || '',
    }),
  };
}
